import { Artefato } from "../../components/Artefato.js";
import { Botao, Falha, Selo } from "../../components/ui.js";
import { DetalheTecnico } from "../../components/DetalheTecnico.js";
import type { Progresso } from "./useGeracao.js";

/**
 * A espera.
 *
 * Aqui o operador não tem nada a fazer além de aguardar, com uma pessoa parada
 * na frente. O que a tela precisa dizer é: ainda estou trabalhando, e falta
 * mais ou menos isto. Por isso o passo textual vem do próprio job — "Enviando ao
 * modelo", "Salvando imagem" — e não um genérico "carregando".
 */
export function EtapaGerar({
  status,
  progresso,
  erro,
  erroTecnico,
  imagemUrl,
  aoGerar,
  aoRefazer,
  aoAprovar,
}: {
  status: "pending" | "processing" | "completed" | "failed";
  progresso: Progresso | null;
  erro?: string | null;
  /** O texto original do provedor; o servidor só o manda para o admin. */
  erroTecnico?: { texto: string; dica: string | null } | null;
  imagemUrl?: string | null;
  aoGerar: () => void;
  aoRefazer: () => void;
  aoAprovar: () => void;
}) {
  if (status === "processing") {
    return (
      <div className="flex flex-col items-center gap-5 py-4">
        <Artefato tamanho="cheio" estado="processando" className="max-w-[15rem]" />

        <div className="w-full max-w-sm">
          <div className="mb-2 flex items-baseline justify-between">
            <p className="text-[0.9375rem] text-texto">{progresso?.step ?? "Começando…"}</p>
            {progresso && (
              <span className="num font-mono text-[0.8125rem] text-projecao">
                {progresso.percent}%
              </span>
            )}
          </div>
          <div className="h-1 overflow-hidden rounded-full bg-borda">
            <div
              className="h-full rounded-full bg-projecao transition-[width] duration-500"
              style={{ width: `${progresso?.percent ?? 5}%` }}
            />
          </div>
        </div>

        <p className="max-w-[32ch] text-center text-[0.875rem] leading-relaxed text-texto-suave">
          Costuma levar de um a três minutos. Pode deixar a tela aberta — ela se atualiza sozinha.
        </p>
      </div>
    );
  }

  if (status === "failed") {
    return (
      <div className="flex flex-col gap-4">
        <Falha mensagem={erro ?? "A geração não foi concluída."} />
        <DetalheTecnico detalhe={erroTecnico} />
        <Botao tom="brasa" largo onClick={aoGerar}>
          Tentar de novo
        </Botao>
      </div>
    );
  }

  if (status === "completed" && imagemUrl) {
    return (
      <div className="flex flex-col gap-4">
        <div className="overflow-hidden rounded-peca border border-borda bg-black">
          <img src={imagemUrl} alt="Imagem gerada" className="w-full object-contain" />
        </div>

        <div className="flex items-center gap-2">
          <Selo tom="fixado">Pronta</Selo>
          <span className="text-[0.8125rem] text-texto-suave">
            Mostre para a pessoa antes de aprovar.
          </span>
        </div>

        <div className="flex gap-3">
          <Botao tom="brasa" largo onClick={aoAprovar}>
            Aprovar
          </Botao>
          <Botao tom="descartar" onClick={aoRefazer}>
            Refazer
          </Botao>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="text-[0.9375rem] leading-relaxed text-texto-suave">
        A foto e o pedido estão prontos. Gerar consome crédito, então confira antes de seguir.
      </p>
      <Botao tom="brasa" largo onClick={aoGerar}>
        Gerar imagem
      </Botao>
    </div>
  );
}
